import {CalendarProps, DateRange, InRangeResult} from '../../types';
import moment from 'moment/moment';
import styles from './base-calendar.module.scss';
import classNames from 'classnames';
import React, { useState } from 'react';
import CalendarNavigation from './navigation';
import Month from './month';
import { getSecondDate } from '../../utils';
import Year from './year';

const getInitialActiveDate = (selectedRange?: DateRange, min?: moment.Moment, max?: moment.Moment, doubled?: boolean): moment.Moment => {
    if (selectedRange) {
        const startOfRange = selectedRange[0];
        if (startOfRange) {
            return startOfRange.clone().startOf('day');
        }
    }
    if (doubled) {
        return moment().add(-1, 'month').startOf('day');
    }
    
    if (max) {
        if (min) {
            const distance = max.diff(min, 'day');
            if (distance > 32) {
                return min.clone().startOf('day');
            }
        }
        return max.clone().startOf('day');
    }

    return moment().startOf('day');
}

const BaseCalendar = (props: CalendarProps) => {
    const { 
        view, 
        size, 
        selectedRange, 
        previewRange, 
        onDateClick, 
        onDayOverEnter, 
        onDayOverLeave, 
        years,
        disabledNavigation,
        showHeader
    } = props;
    const doubled = props.size === 'small' && props.double !== undefined && props.double;
    const [activeDate, setActiveDate] = useState(getInitialActiveDate(selectedRange, props.minDate, props.maxDate, doubled));

    const handleSwitch = React.useCallback((move: 'decrease' | 'increase') => {
        switch (move) {
            case 'decrease':
                setActiveDate(activeDate.clone().add(-1, 'month'));
                break;
            case 'increase':
                setActiveDate(activeDate.clone().add(+1, 'month'));
                break;
        }
    }, [activeDate])

    const handleDayClick = (date: moment.Moment) => {
        onDateClick?.(date);
    }

    const handleDayOverEnter = (date: moment.Moment) => {
        onDayOverEnter?.(date);
    }

    const handleDayOverLeave = (date: moment.Moment) => {
        onDayOverLeave?.(date);
    }

    const getRange = React.useCallback((date: moment.Moment): InRangeResult | undefined => {
        if (!selectedRange && !previewRange) {
            return undefined
        }

        let start: moment.Moment;
        let end: moment.Moment | undefined | null;
        if (previewRange) {
            start = previewRange[0]
            end = previewRange[1]
        } else if (selectedRange) {
            start = selectedRange[0];
            end = selectedRange[1]
        } else {
            return undefined;
        }

        if (!end) {
            return undefined
        }


        const startStamp = start.unix();
        const endStamp = end.unix();

        if (startStamp > endStamp) {
            const temp = start.clone();
            start = end.clone();
            end = temp.clone();
        }

        const startStamp2 = start.unix();
        const endStamp2 = end.unix();

        const currentStamp = date.unix();

        if (currentStamp >= startStamp2 && currentStamp <= endStamp2) {
            const range = {
                commit: previewRange === undefined,
                start: startStamp2 === currentStamp,
                end: endStamp2 === currentStamp
            }
            return range;
        }
        
        return undefined
    }, [activeDate, selectedRange, previewRange])

    const renderContent = (second?: boolean) => {
        const currentActiveDate = second ? getSecondDate(view, activeDate) : activeDate.clone();
        const year = years?.find(x => x.id === currentActiveDate.year());

        switch (view) {
            case 'year': {
                return (<Year 
                    activeDate={currentActiveDate} 
                    onDayClick={handleDayClick} 
                    onDayOverEnter={handleDayOverEnter} 
                    onDayOverLeave={handleDayOverLeave}
                    showHeader={showHeader}
                    year={year}/>)
            }
            case 'month': {
                return (<Month
                    month={year?.months.find(x => x.id === currentActiveDate.month() + 1)}
                    key={currentActiveDate.unix()}
                    activeDate={currentActiveDate}
                    onDayClick={handleDayClick}
                    onDayOverEnter={handleDayOverEnter}
                    onDayOverLeave={handleDayOverLeave}
                    getIsDayInRange={getRange}
                    previewEnd={previewRange && previewRange[1]?.clone()}
                    small={size === 'small' && !doubled}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    dense={props.dense}/>)
            }
        }
    }

    const renderNavigation = () => {
        return(<CalendarNavigation
            activeDate={activeDate}
            onSwitch={handleSwitch}
            view={view}
            doubled={doubled}
            minDate={props.minDate}
            maxDate={props.maxDate} />)
    }

    return (<div className={classNames({
        [styles.container]: true,
        [styles.fullSize]: props.size === 'full',
        [styles.dense]: props.dense
    })}>
        { !disabledNavigation ? renderNavigation() : null}
        <div className={styles.content}>
            {renderContent()}
            { doubled ? <>
                <span className={styles.separatorLine} />
                {renderContent(true)}
            </> : null }
        </div>
    </div>)
}

export default React.memo(BaseCalendar)

